import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { ThunderboltOutlined, EditOutlined } from "@ant-design/icons";
import { colors, formatCurrency, getUserLabel } from "../../../utils/Utils";
import dayjs from "dayjs";
import tenderApis from "../../../api/tender/tenderApis";
import TenderAllocationDrawer from "./TenderAllocationDrawer";
import TenderShortlistedDrawer from "./TenderShortlistedDrawer";
import SupplierCardStrip from "./components/SupplierCardStrip";
import ComparisonTable from "./components/ComparisonTable";
import FullGridView from "./components/FullGridView";
import MaterialAnalysisPanel from "./components/MaterialAnalysisPanel";
import { buildMaterialDataSource, getSupplierGrandTotal, MaterialInfo } from "./components/comparisonUtils";
import InsightsDrawer from "./components/InsightsDrawer";
import StatusIndicatorDrawer, { IndicatorItem } from "./components/StatusIndicatorDrawer";

const TenderComparisonView = ({ tenderID }: { tenderID?: string }) => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [evaluationScoreMap, setEvaluationScoreMap] = useState<
    Record<string, { sections: any[]; overallScore: number; rank: number }>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [materialHierarchyMap, setMaterialHierarchyMap] = useState<Record<number, { unitName: string; floorNo: string; subUnitName: string; locationName: string; needByDate: string }>>({});

  const fetchAllocations = async (supplierList?: any[]) => {
    try {
      const tenderId = Number(tenderID);
      const response = await tenderApis.fetchTenderAllocations(tenderId);
      if (response?.status || response?.data) {
        const allocData = response?.data?.response || response?.data || {};
        const availableSuppliers = supplierList || suppliers;
        const allocations = (allocData?.allocations || allocData?.data || []).map((a: any) => {
          // Derive supplierID: try direct fields first, then match via supplierBidId
          let supplierID = String(a.supplierID || a.supplierId || a.contractorID || "");
          if (!supplierID && a.supplierBidId && availableSuppliers.length > 0) {
            const matched = availableSuppliers.find(
              (s: any) => String(s.bidId) === String(a.supplierBidId)
            );
            if (matched) {
              supplierID = String(matched.supplierID || matched.supplierId || "");
            }
          }
          return {
            allocationID: a.allocationID || a.allocationId || a.id,
            materialName: a.materialName || a.taskTitle || "",
            supplierID,
            supplierName: a.supplierName || a.contractorName || "",
            awardQty: a.awardQty,
            reason: a.reason || "",
            askedQty: a.askedQty || 0,
            unitPrice: a.unitPrice || 0,
            taxPercentage: a.taxPercentage || 0,
            tenderMaterialId: a.tenderMaterialId,
            tenderTaskId: a.tenderTaskId,
            supplierBidId: a.supplierBidId,
            contractorBidId: a.contractorBidId,
          };
        });
        setShortlistedData(allocations);
      }
    } catch (error) {
      console.log("Error fetching allocations:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tenderId = Number(tenderID);

        // Fetch BAFO bid responses, evaluation results, tender details, and indicators in parallel
        const [bidResponse, evalResponse, tenderDetailsResponse, indicatorsResponse] = await Promise.all([
          tenderApis.fetchBafoBidResponses(tenderId),
          tenderApis.getTenderEvaluationResults(tenderId),
          tenderApis.getTenderById(tenderId),
          tenderApis.fetchPriceComparisonIndicators(tenderId),
        ]);

        // Build a map of tenderMaterialId -> hierarchy fields from tender details
        const tenderMaterials: any[] = tenderDetailsResponse?.data?.materials || tenderDetailsResponse?.data?.data?.materials || [];
        const hierarchyMap: Record<number, { unitName: string; floorNo: string; subUnitName: string; locationName: string; needByDate: string }> = {};
        tenderMaterials.forEach((m: any) => {
          if (m.tenderMaterialId) {
            hierarchyMap[m.tenderMaterialId] = {
              unitName: m.unitName || "",
              floorNo: m.floorNo || "",
              subUnitName: m.subUnitName || "",
              locationName: m.locationName || "",
              needByDate: m.needByDate || "",
            };
          }
        });
        setMaterialHierarchyMap(hierarchyMap);

        // Map target prices from tender details
        const initialTargetPrices: Record<number, number> = {};
        const initialTargetInputValues: Record<number, string> = {};
        tenderMaterials.forEach((m: any) => {
          if (m.tenderMaterialId && m.targetPrice != null && m.targetPrice > 0) {
            initialTargetPrices[m.tenderMaterialId] = Number(m.targetPrice);
            initialTargetInputValues[m.tenderMaterialId] = String(m.targetPrice);
          }
        });
        setTargetPrices(initialTargetPrices);
        setTargetInputValues(initialTargetInputValues);

        // Map price comparison indicators from dedicated API
        const indicatorData = indicatorsResponse?.data?.indicators || indicatorsResponse?.data?.data?.indicators || [];
        if (indicatorData.length > 0) {
          setStatusIndicatorRanges(
            indicatorData.map((ind: any) => ({
              title: ind.title,
              color: ind.color,
              startPrice: ind.startPrice,
              endPrice: ind.endPrice,
            }))
          );
        }

        // Process supplier responses
        let supplierList: any[] = [];
        if (bidResponse?.status || bidResponse?.data) {
          const responseData = bidResponse?.data?.response || bidResponse?.data || {};
          supplierList = responseData?.suppliers || responseData?.list || [];
        }
        const mapped = supplierList.map((s: any) => ({
          ...s,
          supplierID: s.supplierId || s.supplierID || s.bidId,
          lineItems: s.lineItems || s.materials || s.bidMaterials || s.items || [],
        }));
        setSuppliers(mapped);

        // Process evaluation results
        let evalSuppliers: any[] = [];
        if (evalResponse?.status || evalResponse?.data) {
          const evalData = evalResponse?.data?.response || evalResponse?.data || {};
          evalSuppliers = evalData?.supplierEvaluations || [];
        }
        const map: Record<string, { sections: any[]; overallScore: number; rank: number }> = {};
        evalSuppliers.forEach((supplier: any) => {
          const supplierId = supplier.supplierId || supplier.supplierID || supplier.bidId;
          const sections = (supplier.sections || []).map((s: any) => ({
            name: s.sectionName,
            score: s.sectionScore,
            maxScore: s.maxScore,
          }));
          const overallScore = supplier.overallScore || (
            sections.length > 0
              ? sections.reduce((sum: number, s: any) => sum + s.score, 0) / sections.length
              : 0
          );
          map[supplierId] = {
            sections,
            overallScore,
            rank: supplier.rank,
          };
        });
        setEvaluationScoreMap(map);

        // Fetch existing allocations and bid versions in parallel
        await Promise.all([fetchAllocations(mapped), fetchBidVersions()]);
      } catch (error) {
        console.log("Error fetching comparison data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (tenderID) fetchData();
  }, [tenderID, refreshKey]);

  const materialNames = useMemo((): MaterialInfo[] => {
    const seen = new Set<number>();
    const result: MaterialInfo[] = [];
    suppliers.forEach((s) => {
      (s.lineItems || []).forEach((li: any) => {
        if (li.tenderMaterialId != null && !seen.has(li.tenderMaterialId)) {
          seen.add(li.tenderMaterialId);
          const hierarchy = materialHierarchyMap[li.tenderMaterialId];
          result.push({
            tenderMaterialId: li.tenderMaterialId,
            materialName: li.materialName,
            usage: li.usage || "",
            unitName: hierarchy?.unitName || "",
            floorNo: hierarchy?.floorNo || "",
            subUnitName: hierarchy?.subUnitName || "",
            locationName: hierarchy?.locationName || "",
            needByDate: hierarchy?.needByDate || "",
          });
        }
      });
    });
    return result;
  }, [suppliers, materialHierarchyMap]);

  // Site hierarchy filters
  const [selectedTower, setSelectedTower] = useState<string | undefined>(undefined);
  const [selectedFloor, setSelectedFloor] = useState<string | undefined>(undefined);
  const [selectedFlat, setSelectedFlat] = useState<string | undefined>(undefined);

  // Detect flow type: villa vs tower based on hierarchy data
  const isVillaFlow = useMemo(() => {
    return materialNames.some(
      (m) => m.locationName && !m.unitName && !m.floorNo && !m.subUnitName
    );
  }, [materialNames]);

  // Derive unique filter options from materialNames
  const hierarchyFilterOptions = useMemo(() => {
    const towers = new Set<string>();
    const floors = new Set<string>();
    const flats = new Set<string>();
    const villas = new Set<string>();
    materialNames.forEach((m) => {
      if (m.unitName) towers.add(m.unitName);
      if (m.floorNo) floors.add(m.floorNo);
      if (m.subUnitName) flats.add(m.subUnitName);
      if (m.locationName) villas.add(m.locationName);
    });
    return {
      towers: Array.from(towers).sort(),
      floors: Array.from(floors).sort((a, b) => Number(a) - Number(b)),
      flats: Array.from(flats).sort(),
      villas: Array.from(villas).sort(),
    };
  }, [materialNames]);

  // Filter materialNames based on selected hierarchy
  const filteredMaterialNames = useMemo(() => {
    return materialNames.filter((m) => {
      if (isVillaFlow) {
        if (selectedTower && m.locationName !== selectedTower) return false;
      } else {
        if (selectedTower && m.unitName !== selectedTower) return false;
        if (selectedFloor && m.floorNo !== selectedFloor) return false;
        if (selectedFlat && m.subUnitName !== selectedFlat) return false;
      }
      return true;
    });
  }, [materialNames, selectedTower, selectedFloor, selectedFlat, isVillaFlow]);

  // Cascading: floors and flats filtered by selected tower/floor
  const availableFloors = useMemo(() => {
    if (!selectedTower) return hierarchyFilterOptions.floors;
    const floors = new Set<string>();
    materialNames.forEach((m) => {
      if (m.unitName === selectedTower && m.floorNo) floors.add(m.floorNo);
    });
    return Array.from(floors).sort((a, b) => Number(a) - Number(b));
  }, [selectedTower, materialNames, hierarchyFilterOptions.floors]);

  const availableFlats = useMemo(() => {
    const flats = new Set<string>();
    materialNames.forEach((m) => {
      if (selectedTower && m.unitName !== selectedTower) return;
      if (selectedFloor && m.floorNo !== selectedFloor) return;
      if (m.subUnitName) flats.add(m.subUnitName);
    });
    return Array.from(flats).sort();
  }, [selectedTower, selectedFloor, materialNames]);

  // --- Existing state (unchanged) ---
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [allocationDrawerVisible, setAllocationDrawerVisible] = useState(false);
  const [shortlistedDrawerVisible, setShortlistedDrawerVisible] =
    useState(false);
  const [shortlistedData, setShortlistedData] = useState<any[]>([]);

  // --- New state ---
  const [insightsDrawerVisible, setInsightsDrawerVisible] = useState(false);
  const [supplierDetailsDrawerVisible, setSupplierDetailsDrawerVisible] = useState(false);
  const [viewMode, setViewMode] = useState<"dashboard" | "grid">("dashboard");
  const [selectedSupplierIDs, setSelectedSupplierIDs] = useState<string[]>([]);
  const [cardSortBy, setCardSortBy] = useState<"rank" | "price" | "score">(
    "rank"
  );
  const [versionsDrawerVisible, setVersionsDrawerVisible] = useState(false);
  const [versionsData, setVersionsData] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Target price & status indicator state
  const [targetPrices, setTargetPrices] = useState<Record<number, number>>({});
  const [targetInputValues, setTargetInputValues] = useState<Record<number, string>>({});
  const [statusIndicatorDrawerVisible, setStatusIndicatorDrawerVisible] = useState(false);
  const [statusIndicatorRanges, setStatusIndicatorRanges] = useState<IndicatorItem[]>([]);
  const [indicatorSaving, setIndicatorSaving] = useState(false);

  const fetchBidVersions = async () => {
    try {
      setVersionsLoading(true);
      const response = await tenderApis.fetchBidVersions(Number(tenderID));
      if (response?.status || response?.data?.status) {
        const data = response?.data?.data || response?.data || {};
        setVersionsData(data?.suppliers || []);
      }
    } catch (error) {
      console.log("Error fetching bid versions:", error);
    } finally {
      setVersionsLoading(false);
    }
  };

  // --- Auto-select top 3 ranked on mount ---
  useEffect(() => {
    if (suppliers.length === 0) return;
    const ranked = [...suppliers].sort((a, b) => {
      const rankA = evaluationScoreMap[a.supplierID]?.rank ?? 999;
      const rankB = evaluationScoreMap[b.supplierID]?.rank ?? 999;
      return rankA - rankB;
    });
    setSelectedSupplierIDs(ranked.slice(0, 3).map((s) => s.supplierID));
  }, [suppliers.length, evaluationScoreMap]);

  // --- Filtered and sorted suppliers for dashboard view ---
  const filteredSuppliers = useMemo(() => {
    const filtered = suppliers.filter((s) =>
      selectedSupplierIDs.includes(s.supplierID)
    );
    switch (cardSortBy) {
      case "rank":
        return filtered.sort((a, b) => {
          const rankA = evaluationScoreMap[a.supplierID]?.rank ?? 999;
          const rankB = evaluationScoreMap[b.supplierID]?.rank ?? 999;
          return rankA - rankB;
        });
      case "price":
        return filtered.sort((a, b) => {
          const totalA = getSupplierGrandTotal(a, materialNames);
          const totalB = getSupplierGrandTotal(b, materialNames);
          return totalA - totalB;
        });
      case "score":
        return filtered.sort((a, b) => {
          const scoreA = evaluationScoreMap[a.supplierID]?.overallScore ?? 0;
          const scoreB = evaluationScoreMap[b.supplierID]?.overallScore ?? 0;
          return scoreB - scoreA;
        });
      default:
        return filtered;
    }
  }, [suppliers, selectedSupplierIDs, cardSortBy, evaluationScoreMap, materialNames]);

  // --- Data source for allocation drawer (uses all suppliers) ---
  const dataSource = useMemo(
    () => buildMaterialDataSource(materialNames, suppliers, shortlistedData),
    [materialNames, suppliers, shortlistedData]
  );

  // --- Existing handlers (unchanged) ---
  const handleMaterialSelection = (key: string, checked: boolean) => {
    if (checked) {
      setSelectedMaterials((prev) => [...prev, key]);
    } else {
      setSelectedMaterials((prev) => prev.filter((k) => k !== key));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setIsAllSelected(checked);
    if (checked) {
      const selectable = dataSource
        .filter((row) => row.remainingQty > 0)
        .map((row) => row.key);
      setSelectedMaterials(selectable);
    } else {
      setSelectedMaterials([]);
    }
  };

  const openAllocationDrawer = (e: any) => {
    e.preventDefault();
    if (selectedMaterials.length === 0) {
      message.warning("Please select at least one material to allocate.");
      return;
    }
    setAllocationDrawerVisible(true);
  };

  const handleAllocationSubmit = async (values: any[]) => {
    try {
      const tenderId = Number(tenderID);
      const allocations = values.map((item) => {
        const supplier = suppliers.find(
          (s) => s.supplierID === item.supplierID || String(s.supplierID) === String(item.supplierID)
        );
        const lineItem = (supplier?.lineItems || []).find(
          (li: any) => li.tenderMaterialId === item.tenderMaterialId || li.materialName === item.materialName
        );
        const existingAlloc = shortlistedData.find(
          (a) => a.tenderMaterialId === item.tenderMaterialId || a.materialName === item.materialName
        );
        return {
          tenderMaterialId: item.tenderMaterialId || lineItem?.tenderMaterialId || existingAlloc?.tenderMaterialId,
          tenderTaskId: lineItem?.tenderTaskId || existingAlloc?.tenderTaskId,
          supplierBidId: supplier?.bidId || supplier?.supplierId,
          awardQty: Number(item.awardQty),
          reason: item.reason || "",
        };
      });

      const response = await tenderApis.createAllocation({
        tenderId,
        allocations,
      });

      if (response?.status || response?.data?.status) {
        message.success("Successfully allocated!");
        setSelectedMaterials([]);
        setIsAllSelected(false);
        // Refetch allocations to get updated data with allocationIDs
        await fetchAllocations();
      } else {
        message.error(response?.message || response?.data?.message || "Failed to create allocation");
      }
    } catch (error) {
      console.log("Error creating allocation:", error);
      message.error("Failed to create allocation");
    }
  };

  const showShortlistedDrawer = () => {
    if (shortlistedData.length === 0) {
      message.warning("There are no shortlisted suppliers.");
      return;
    }
    setShortlistedDrawerVisible(true);
  };

  const handleRemoveShortlisted = async (index: number) => {
    const item = shortlistedData[index];
    if (item?.allocationID) {
      try {
        const response = await tenderApis.deleteAllocation(item.allocationID);
        if (response?.status || response?.data?.status) {
          message.success("Allocation removed successfully");
          setShortlistedData((prev) => prev.filter((_, i) => i !== index));
        } else {
          message.error(response?.message || "Failed to remove allocation");
        }
      } catch (error) {
        console.log("Error deleting allocation:", error);
        message.error("Failed to remove allocation");
      }
    } else {
      setShortlistedData((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleEditShortlisted = async (index: number, updated: any) => {
    const item = shortlistedData[index];
    if (item?.allocationID) {
      try {
        const response = await tenderApis.updateAllocation(item.allocationID, {
          awardQty: updated.awardQty,
          reason: updated.reason,
        });
        if (response?.status || response?.data?.status) {
          message.success("Allocation updated successfully");
          setShortlistedData((prev) =>
            prev.map((it, i) => (i === index ? { ...it, ...updated } : it))
          );
        } else {
          message.error(response?.message || "Failed to update allocation");
        }
      } catch (error) {
        console.log("Error updating allocation:", error);
        message.error("Failed to update allocation");
      }
    } else {
      setShortlistedData((prev) =>
        prev.map((it, i) => (i === index ? updated : it))
      );
    }
  };

  // --- Target price handlers ---
  const updateTargetPrice = (tenderMaterialId: number, value: string) => {
    setTargetInputValues((prev) => ({ ...prev, [tenderMaterialId]: value }));
    setTargetPrices((prev) => ({ ...prev, [tenderMaterialId]: Number(value) || 0 }));
  };

  const handleTargetPriceBlur = (tenderMaterialId: number) => {
    const value = targetInputValues[tenderMaterialId] === "" ? "0" : targetInputValues[tenderMaterialId];
    setTargetPrices((prev) => ({ ...prev, [tenderMaterialId]: Number(value) }));
  };

  const saveTargetPriceValue = async () => {
    const lines = materialNames
      .filter((mat) => targetPrices[mat.tenderMaterialId] > 0)
      .map((mat) => ({
        lineId: mat.tenderMaterialId,
        targetPrice: targetPrices[mat.tenderMaterialId],
      }));
    if (lines.length === 0) {
      message.warning("No materials to set target price.");
      return;
    }
    try {
      const response = await tenderApis.addTargetPrice({
        tenderId: Number(tenderID),
        tenderFor: "MATERIAL",
        lines,
      });
      if (response?.status || response?.data?.status) {
        message.success("Target Price saved successfully!");
      } else {
        message.error("Failed to save target price.");
      }
    } catch (error) {
      console.log("Error saving target price:", error);
      message.error("Failed to save target price.");
    }
  };

  const handleStatusIndicatorSubmit = async (indicators: IndicatorItem[]) => {
    setIndicatorSaving(true);
    try {
      const response = await tenderApis.savePriceComparisonIndicators(
        Number(tenderID),
        indicators
      );
      if (response?.status || response?.data?.status) {
        setStatusIndicatorRanges(indicators);
        setStatusIndicatorDrawerVisible(false);
        message.success("Comparison indicators saved successfully!");
      } else {
        message.error("Failed to save comparison indicators.");
      }
    } catch (error) {
      console.log("Error saving indicators:", error);
      message.error("Failed to save comparison indicators.");
    } finally {
      setIndicatorSaving(false);
    }
  };

  // --- Computed values ---
  const shortlistedSupplierCount = new Set(
    shortlistedData.map((item) => item.supplierID)
  ).size;

  const shortlistedSupplierIDs = useMemo(
    () => Array.from(new Set(shortlistedData.map((s) => s.supplierID))),
    [shortlistedData]
  );

  const selectedMaterialsForDrawer = dataSource
    .filter((row) => selectedMaterials.includes(row.key))
    .map((row) => ({
      key: row.key,
      materialName: row.materialName,
      tenderMaterialId: row.tenderMaterialId,
      askedQty: row.askedQty,
      remainingQty: row.remainingQty,
    }));

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="table-container">
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
                <Space size="middle" wrap align="center">
                  <Segmented
                    options={[
                      { label: "Dashboard", value: "dashboard" },
                      { label: "Full Grid", value: "grid" },
                    ]}
                    value={viewMode}
                    onChange={(val) => setViewMode(val as any)}
                  />
                  {viewMode === "dashboard" && (
                    <Select
                      value={cardSortBy}
                      onChange={setCardSortBy}
                      style={{ width: 150 }}
                    >
                      <Select.Option value="rank">Sort: Rank</Select.Option>
                      <Select.Option value="price">Sort: Price</Select.Option>
                      <Select.Option value="score">Sort: Score</Select.Option>
                    </Select>
                  )}
                  {/* Supplier Icons — only show when bid versions have suppliers */}
                  {versionsData.length > 0 && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: 10,
                        }}
                      >
                        {versionsData.slice(0, 4).map((supplier: any, index: number) => (
                          <Tooltip
                            key={supplier.supplierBidId || supplier.supplierID || index}
                            title={supplier.supplierName}
                          >
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                backgroundColor: colors[index % colors.length],
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                marginLeft: index > 0 ? -10 : 0,
                                zIndex: versionsData.length - index,
                                border: "2px solid #fff",
                                cursor: "pointer",
                              }}
                            >
                              {getUserLabel(supplier.supplierName)}
                            </div>
                          </Tooltip>
                        ))}
                        {versionsData.length > 4 && (
                          <div style={{ marginLeft: 10, fontSize: 14 }}>
                            +{versionsData.length - 4} Suppliers
                          </div>
                        )}
                      </div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setVersionsDrawerVisible(true);
                        }}
                        style={{ fontWeight: 500, whiteSpace: "nowrap" }}
                      >
                        View More Details
                      </a>
                    </>
                  )}
                </Space>
                <Space size="middle" wrap align="center">
                  {/* Status Indicator Legend */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {(statusIndicatorRanges.length > 0
                      ? statusIndicatorRanges
                      : [
                          { title: "Slightly High", color: "#f2d1ae", startPrice: 0, endPrice: 0 },
                          { title: "High", color: "#f4e065", startPrice: 0, endPrice: 0 },
                          { title: "Very High", color: "#faad14", startPrice: 0, endPrice: 0 },
                          { title: "Outlier", color: "#ee545b", startPrice: 0, endPrice: 0 },
                        ]
                    ).map((ind, idx) => (
                      <Tooltip
                        key={idx}
                        title={
                          ind.startPrice > 0 || ind.endPrice > 0
                            ? `${ind.startPrice.toLocaleString()} – ${ind.endPrice.toLocaleString()}`
                            : ind.title
                        }
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                          <span style={{ width: 12, height: 12, backgroundColor: ind.color, display: "inline-block", borderRadius: 2 }} />
                          {ind.title}
                        </div>
                      </Tooltip>
                    ))}
                    <Tooltip title="Edit Indicators">
                      <EditOutlined
                        style={{ cursor: "pointer", color: "#1890ff" }}
                        onClick={() => setStatusIndicatorDrawerVisible(true)}
                      />
                    </Tooltip>
                  </div>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      saveTargetPriceValue();
                    }}
                    style={{ fontWeight: 500 }}
                  >
                    Save Target Price
                  </a>
                  <Button
                    type="default"
                    icon={<ThunderboltOutlined />}
                    onClick={() => setInsightsDrawerVisible(true)}
                    style={{ fontWeight: 500 }}
                  >
                    Insights
                  </Button>
                  <a
                    href="#"
                    onClick={openAllocationDrawer}
                    style={{ fontWeight: 500 }}
                  >
                    Allocate / Shortlist
                  </a>
                  <Tooltip title="Click to view shortlisted suppliers">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        showShortlistedDrawer();
                      }}
                      style={{ fontWeight: 500 }}
                    >
                      Shortlisted Suppliers: {shortlistedSupplierCount}
                    </a>
                  </Tooltip>
                </Space>
              </div>

              {/* View Content */}
              {viewMode === "dashboard" ? (
                <>
                  <SupplierCardStrip
                    suppliers={suppliers}
                    evaluationScoreMap={evaluationScoreMap}
                    materialNames={materialNames}
                    selectedSupplierIDs={selectedSupplierIDs}
                    onSelectionChange={setSelectedSupplierIDs}
                    shortlistedSupplierIDs={shortlistedSupplierIDs}
                    sortBy={cardSortBy}
                  />
                  <ComparisonTable
                    suppliers={filteredSuppliers}
                    evaluationScoreMap={evaluationScoreMap}
                    materialNames={filteredMaterialNames}
                    selectedMaterials={selectedMaterials}
                    onMaterialSelection={handleMaterialSelection}
                    onSelectAll={handleSelectAll}
                    isAllSelected={isAllSelected}
                    shortlistedData={shortlistedData}
                    tenderID={tenderID}
                    onDiscountApplied={() => setRefreshKey((k) => k + 1)}
                    targetInputValues={targetInputValues}
                    onTargetPriceChange={updateTargetPrice}
                    onTargetPriceBlur={handleTargetPriceBlur}
                    targetPrices={targetPrices}
                    statusIndicatorRanges={statusIndicatorRanges}
                    isVillaFlow={isVillaFlow}
                    hierarchyFilterOptions={hierarchyFilterOptions}
                    selectedTower={selectedTower}
                    selectedFloor={selectedFloor}
                    selectedFlat={selectedFlat}
                    onTowerChange={(val) => {
                      setSelectedTower(val);
                      setSelectedFloor(undefined);
                      setSelectedFlat(undefined);
                    }}
                    onFloorChange={(val) => {
                      setSelectedFloor(val);
                      setSelectedFlat(undefined);
                    }}
                    onFlatChange={setSelectedFlat}
                    availableFloors={availableFloors}
                    availableFlats={availableFlats}
                    totalMaterialCount={materialNames.length}
                  />
                  <MaterialAnalysisPanel
                    suppliers={filteredSuppliers}
                    evaluationScoreMap={evaluationScoreMap}
                    materialNames={filteredMaterialNames}
                  />
                </>
              ) : (
                <FullGridView
                  suppliers={suppliers}
                  evaluationScoreMap={evaluationScoreMap}
                  materialNames={filteredMaterialNames}
                  selectedMaterials={selectedMaterials}
                  onMaterialSelection={handleMaterialSelection}
                  onSelectAll={handleSelectAll}
                  isAllSelected={isAllSelected}
                  shortlistedData={shortlistedData}
                />
              )}

      {/* Allocation / Shortlist Drawer */}
      {allocationDrawerVisible && (
        <TenderAllocationDrawer
          visible={allocationDrawerVisible}
          onClose={() => setAllocationDrawerVisible(false)}
          materials={selectedMaterialsForDrawer}
          suppliers={suppliers}
          onSubmit={handleAllocationSubmit}
        />
      )}

      {/* Shortlisted Suppliers Drawer */}
      {shortlistedDrawerVisible && (
        <TenderShortlistedDrawer
          visible={shortlistedDrawerVisible}
          onClose={() => setShortlistedDrawerVisible(false)}
          shortlistedData={shortlistedData}
          onRemove={handleRemoveShortlisted}
          onEdit={handleEditShortlisted}
        />
      )}

      {/* Supplier Details Drawer */}
      <Drawer
        title="Supplier Details"
        width="80%"
        onClose={() => setSupplierDetailsDrawerVisible(false)}
        open={supplierDetailsDrawerVisible}
      >
        <Table
          columns={[
            {
              title: "Supplier",
              dataIndex: "supplierName",
              key: "supplierName",
              render: (text: string, _record: any, index: number) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor: colors[index % colors.length],
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {getUserLabel(text)}
                  </div>
                  <span>{text}</span>
                </div>
              ),
            },
            {
              title: "Rank",
              key: "rank",
              render: (_: any, record: any) => {
                const rank = evaluationScoreMap[record.supplierID]?.rank;
                return rank ?? "N/A";
              },
              sorter: (a: any, b: any) =>
                (evaluationScoreMap[a.supplierID]?.rank ?? 999) -
                (evaluationScoreMap[b.supplierID]?.rank ?? 999),
            },
            {
              title: "Evaluation Score",
              key: "score",
              render: (_: any, record: any) => {
                const score = evaluationScoreMap[record.supplierID]?.overallScore;
                return score != null ? `${score.toFixed(2)}` : "N/A";
              },
              sorter: (a: any, b: any) =>
                (evaluationScoreMap[a.supplierID]?.overallScore ?? 0) -
                (evaluationScoreMap[b.supplierID]?.overallScore ?? 0),
            },
            {
              title: "Grand Total",
              key: "grandTotal",
              render: (_: any, record: any) => {
                const total = getSupplierGrandTotal(record, materialNames);
                return formatCurrency(total, "INR");
              },
              sorter: (a: any, b: any) =>
                getSupplierGrandTotal(a, materialNames) -
                getSupplierGrandTotal(b, materialNames),
            },
            {
              title: "Materials Quoted",
              key: "materialsQuoted",
              render: (_: any, record: any) => {
                const quoted = (record.lineItems || []).length;
                return `${quoted} / ${materialNames.length}`;
              },
            },
            {
              title: "Payment Terms",
              dataIndex: "paymentTerms",
              key: "paymentTerms",
              render: (text: any) => text || "N/A",
            },
          ]}
          dataSource={suppliers}
          rowKey="supplierID"
          expandable={{
            expandedRowRender: (record: any) => (
              <Table
                style={{ margin: "2%" }}
                columns={[
                  {
                    title: "Material",
                    dataIndex: "materialName",
                    key: "materialName",
                  },
                  {
                    title: "Usage",
                    dataIndex: "usage",
                    key: "usage",
                    width: 150,
                    ellipsis: true,
                    render: (text: any) => {
                      const val = text || "-";
                      if (val.length > 20) {
                        return (
                          <Tooltip title={val}>
                            <span style={{ cursor: "pointer" }}>{val.slice(0, 20)}...</span>
                          </Tooltip>
                        );
                      }
                      return val;
                    },
                  },
                  {
                    title: "Quoted Qty",
                    dataIndex: "quotedQuantity",
                    key: "quotedQuantity",
                  },
                  {
                    title: "Unit Price",
                    dataIndex: "quotedUnitPrice",
                    key: "quotedUnitPrice",
                    render: (text: any) => formatCurrency(text, "INR"),
                  },
                  {
                    title: "Tax %",
                    dataIndex: "taxPercentage",
                    key: "taxPercentage",
                    render: (text: any) => (text != null ? `${text}%` : "N/A"),
                  },
                  {
                    title: "UOM",
                    dataIndex: "uom",
                    key: "uom",
                    render: (text: any) => text || "N/A",
                  },
                ]}
                dataSource={record.lineItems || []}
                pagination={false}
                size="small"
                bordered
                rowKey="tenderMaterialId"
              />
            ),
          }}
        />
      </Drawer>

      {/* Versions Drawer */}
      <Drawer
        title="Supplier Bid Versions"
        width="80%"
        onClose={() => setVersionsDrawerVisible(false)}
        open={versionsDrawerVisible}
      >
        {versionsLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : versionsData.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
            No bid versions found for this tender.
          </div>
        ) : (
          <Table
            columns={[
              {
                title: "Supplier",
                dataIndex: "supplierName",
                key: "supplierName",
                fixed: "left" as const,
                width: 300,
                render: (text: string, record: any, index: number) => {
                  const possibleIds = [
                    record.supplierId,
                    record.supplierID,
                    record.supplierBidId,
                    record.bidId,
                  ].filter(Boolean);
                  let evalData = possibleIds.reduce<any>(
                    (found, id) => found || evaluationScoreMap[String(id)],
                    null
                  );
                  // Fallback: match by supplier name via suppliers array
                  if (!evalData && text) {
                    const matched = suppliers.find(
                      (s: any) => s.supplierName === text
                    );
                    if (matched) {
                      evalData = evaluationScoreMap[String(matched.supplierID)];
                    }
                  }
                  const rankColor =
                    evalData?.rank === 1
                      ? "gold"
                      : evalData?.rank === 2
                      ? "default"
                      : evalData?.rank === 3
                      ? "orange"
                      : "blue";
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          backgroundColor: colors[index % colors.length],
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {getUserLabel(text)}
                      </div>
                      <span style={{ fontWeight: 500 }}>{text}</span>
                      {evalData && (
                        <>
                          <Tag color={rankColor} style={{ fontSize: 11, margin: 0 }}>
                            Rank #{evalData.rank}
                          </Tag>
                          <Tag style={{ fontSize: 11, margin: 0 }}>
                            {evalData.overallScore.toFixed(1)}/100
                          </Tag>
                        </>
                      )}
                    </div>
                  );
                },
              },
              {
                title: "Initial Value",
                key: "initialValue",
                align: "right" as const,
                width: 160,
                render: (_: any, record: any) => {
                  const versions = record.versions || [];
                  if (versions.length === 0) return "-";
                  const first = versions[0];
                  return first?.taxableAmount != null ? formatCurrency(first.taxableAmount, "INR") : "-";
                },
              },
              {
                title: "Final Value",
                key: "finalValue",
                align: "right" as const,
                width: 160,
                render: (_: any, record: any) => {
                  const versions = record.versions || [];
                  if (versions.length === 0) return "-";
                  const last = versions[versions.length - 1];
                  const revisedTaxable =
                    last?.taxableAmount != null
                      ? last.taxableAmount - (last.incrementalDiscountAmount || 0)
                      : null;
                  return revisedTaxable != null ? formatCurrency(revisedTaxable, "INR") : "-";
                },
              },
              {
                title: "Saving",
                key: "saving",
                align: "right" as const,
                width: 160,
                render: (_: any, record: any) => {
                  const versions = record.versions || [];
                  if (versions.length === 0) return "-";
                  const initial = versions[0]?.taxableAmount;
                  const last = versions[versions.length - 1];
                  const finalTaxable =
                    last?.taxableAmount != null
                      ? last.taxableAmount - (last.incrementalDiscountAmount || 0)
                      : null;
                  if (initial == null || finalTaxable == null) return "-";
                  const saving = Math.abs(initial - finalTaxable);
                  const savingPercent = initial !== 0 ? ((saving / initial) * 100).toFixed(2) : "0.0";
                  return (
                    <span style={{ color: "#52c41a", fontWeight: 600 }}>
                      {formatCurrency(saving, "INR")} <span style={{ fontSize: 12 }}>({savingPercent}%)</span>
                    </span>
                  );
                },
              },
              {
                title: "Current Version",
                dataIndex: "currentVersion",
                key: "currentVersion",
                align: "center" as const,
                width: 120,
                render: (text: any) => <b>V{text}</b>,
              },
              {
                title: "Total Versions",
                key: "totalVersions",
                align: "center" as const,
                width: 110,
                render: (_: any, record: any) => (record.versions || []).length,
              },
            ]}
            dataSource={versionsData}
            rowKey={(record) => record.supplierBidId || record.supplierId}
            expandable={{
              expandedRowRender: (record: any) => {
                const versions = record.versions || [];
                if (versions.length === 0) {
                  return <div style={{ padding: 12, color: "#999" }}>No version history available.</div>;
                }
                // Build bidMaterialId -> materialName map from supplier lineItems
                const matchedSupplier = suppliers.find(
                  (s: any) => s.supplierID === record.supplierId || s.supplierName === record.supplierName
                );
                const bidMatMap: Record<number, { name: string; baseAmount: number; usage: string }> = {};
                (matchedSupplier?.lineItems || []).forEach((li: any) => {
                  const id = li.bidMaterialId || li.supplierBidMaterialId || li.id || li.lineItemId;
                  if (id != null) {
                    const qty = li.quotedQuantity || 0;
                    const unitPrice = li.quotedUnitPrice || 0;
                    const installationCharges = li.totalInstallationCharges || 0;
                    bidMatMap[id] = {
                      name: li.materialName || `Material #${id}`,
                      baseAmount: qty * unitPrice + installationCharges,
                      usage: li.usage || "",
                    };
                  }
                });
                return (
                  <Table
                    style={{ margin: "1%" }}
                    columns={[
                      {
                        title: "Version",
                        dataIndex: "version",
                        key: "version",
                        width: 80,
                        align: "center" as const,
                        render: (text: any) => <b>V{text}</b>,
                      },
                      {
                        title: "Previous Amount",
                        dataIndex: "taxableAmount",
                        key: "taxableAmount",
                        align: "right" as const,
                        render: (text: any) => (text != null ? formatCurrency(text, "INR") : "-"),
                      },
                      // {
                      //   title: "Gross Amount",
                      //   dataIndex: "grossAmount",
                      //   key: "grossAmount",
                      //   align: "right" as const,
                      //   render: (text: any) => (text != null ? formatCurrency(text, "INR") : "-"),
                      // },
                      {
                        title: "Discount (%)",
                        dataIndex: "incrementalDiscountPercent",
                        key: "incrementalDiscountPercent",
                        align: "center" as const,
                        width: 130,
                        render: (text: any, row: any) => {
                          if (text == null || text === 0) {
                            return <span style={{ color: "#8c8c8c", fontWeight: 500 }}>0%</span>;
                          }
                          const isPositive = text > 0;
                          const color = isPositive ? "#389e0d" : "#d4380d";
                          const arrow = isPositive ? "↑" : "↓";
                          const prefix = isPositive ? "+" : "";
                          const versionIndex = versions.findIndex((v: any) => v.version === row.version);
                          const prevVersion = versionIndex > 0 ? versions[versionIndex - 1] : null;
                          const prevDiscount = prevVersion?.additionalDiscount ?? 0;
                          const currentDiscount = row.additionalDiscount ?? 0;
                          return (
                            <Tooltip title={
                              <div>
                                <div>Previous: {prevDiscount}%</div>
                                <div>Current: {currentDiscount}%</div>
                                <div>Change: {prefix}{text}%</div>
                              </div>
                            }>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                <span style={{ color, fontWeight: 700, fontSize: 16 }}>{arrow}</span>
                                <span style={{ fontWeight: 600 }}>{prefix}{text}%</span>
                              </span>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        title: "Discount Amount",
                        dataIndex: "incrementalDiscountAmount",
                        key: "incrementalDiscountAmount",
                        align: "right" as const,
                        render: (text: any, row: any) => {
                          if (text == null || text === 0) {
                            return <span style={{ color: "#8c8c8c", fontWeight: 500 }}>-</span>;
                          }
                          const isPositive = text > 0;
                          const color = isPositive ? "#389e0d" : "#d4380d";
                          const arrow = isPositive ? "↑" : "↓";
                          const versionIndex = versions.findIndex((v: any) => v.version === row.version);
                          const prevVersion = versionIndex > 0 ? versions[versionIndex - 1] : null;
                          const prevAmount = prevVersion?.incrementalDiscountAmount ?? 0;
                          return (
                            <Tooltip title={
                              <div>
                                <div>Previous: {formatCurrency(Math.abs(prevAmount), "INR")}</div>
                                <div>Current: {formatCurrency(Math.abs(text), "INR")}</div>
                                <div>Change: {isPositive ? "+" : "-"} {formatCurrency(Math.abs(text - prevAmount), "INR")}</div>
                              </div>
                            }>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                <span style={{ color, fontWeight: 700, fontSize: 16 }}>{arrow}</span>
                                <span style={{ fontWeight: 600 }}>
                                  {isPositive ? "+" : "-"} {formatCurrency(Math.abs(text), "INR")}
                                </span>
                              </span>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        title: "Revised Amount",
                        key: "revisedTaxableAmount",
                        align: "right" as const,
                        render: (_: any, row: any) => {
                          const revisedTaxable =
                            row.taxableAmount != null
                              ? row.taxableAmount - (row.incrementalDiscountAmount || 0)
                              : null;
                          const changed = row.incrementalDiscountPercent != null && row.incrementalDiscountPercent !== 0;
                          return (
                            <b style={{ color: changed ? "#52c41a" : "inherit" }}>
                              {revisedTaxable != null ? formatCurrency(revisedTaxable, "INR") : "-"}
                            </b>
                          );
                        },
                      },
                      {
                        title: "Reason",
                        dataIndex: "revisionReason",
                        key: "revisionReason",
                        width: 200,
                        render: (text: any) => text || "-",
                      },
                      {
                        title: "Submitted At",
                        dataIndex: "submittedAt",
                        key: "submittedAt",
                        width: 150,
                        render: (text: any) => {
                          if (!text) return "-";
                          return dayjs(text).format("DD-MMM-YYYY HH:mm");
                        },
                      },
                    ]}
                    dataSource={versions}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ x: "max-content" }}
                    rowKey={(r: any) => r.version}
                    expandable={{
                      expandedRowRender: (versionRow: any) => {
                        const matDiscounts = versionRow.materialDiscounts || [];
                        if (matDiscounts.length === 0) {
                          return <div style={{ padding: 8, color: "#999" }}>No material-level discounts for this version.</div>;
                        }
                        return (
                          <Table
                            style={{ margin: "0 2%" }}
                            columns={[
                              {
                                title: "Material",
                                key: "materialName",
                                width: 250,
                                render: (_: any, row: any) => bidMatMap[row.bidMaterialId]?.name || `Material #${row.bidMaterialId}`,
                              },
                              {
                                title: "Usage",
                                key: "usage",
                                width: 150,
                                render: (_: any, row: any) => {
                                  const val = bidMatMap[row.bidMaterialId]?.usage || "-";
                                  if (val.length > 20) {
                                    return (
                                      <Tooltip title={val}>
                                        <span style={{ cursor: "pointer" }}>{val.slice(0, 20)}...</span>
                                      </Tooltip>
                                    );
                                  }
                                  return val;
                                },
                              },
                              {
                                title: "Previous Amount",
                                key: "previousAmount",
                                align: "right" as const,
                                width: 160,
                                render: (_: any, row: any) => {
                                  const base = bidMatMap[row.bidMaterialId]?.baseAmount;
                                  if (base == null) return "-";
                                  const prevAmt = base - base * ((row.previousDiscount ?? 0) / 100);
                                  return formatCurrency(prevAmt, "INR");
                                },
                              },
                              {
                                title: "Previous Discount (%)",
                                dataIndex: "previousDiscount",
                                key: "previousDiscount",
                                align: "center" as const,
                                width: 150,
                                render: (text: any) => (text != null ? `${text}%` : "-"),
                              },
                              {
                                title: "New Discount (%)",
                                dataIndex: "newDiscount",
                                key: "newDiscount",
                                align: "center" as const,
                                width: 150,
                                render: (text: any) => (text != null ? `${text}%` : "-"),
                              },
                              {
                                title: "Change (%)",
                                key: "change",
                                align: "center" as const,
                                width: 130,
                                render: (_: any, row: any) => {
                                  const change = (row.newDiscount ?? 0) - (row.previousDiscount ?? 0);
                                  if (change === 0) {
                                    return <span style={{ color: "#8c8c8c", fontWeight: 500 }}>0%</span>;
                                  }
                                  const isPositive = change > 0;
                                  const color = isPositive ? "#389e0d" : "#d4380d";
                                  const arrow = isPositive ? "↑" : "↓";
                                  const prefix = isPositive ? "+" : "";
                                  return (
                                    <Tooltip title={
                                      <div>
                                        <div>Previous: {row.previousDiscount ?? 0}%</div>
                                        <div>Current: {row.newDiscount ?? 0}%</div>
                                        <div>Change: {prefix}{change}%</div>
                                      </div>
                                    }>
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                        <span style={{ color, fontWeight: 700, fontSize: 16 }}>{arrow}</span>
                                        <span style={{ fontWeight: 600 }}>{prefix}{change}%</span>
                                      </span>
                                    </Tooltip>
                                  );
                                },
                              },
                              {
                                title: "Revised Amount",
                                key: "revisedAmount",
                                align: "right" as const,
                                width: 160,
                                render: (_: any, row: any) => {
                                  const base = bidMatMap[row.bidMaterialId]?.baseAmount;
                                  if (base == null) return "-";
                                  const revisedAmt = base - base * ((row.newDiscount ?? 0) / 100);
                                  const changed = (row.newDiscount ?? 0) !== (row.previousDiscount ?? 0);
                                  return (
                                    <b style={{ color: changed ? "#52c41a" : "inherit" }}>
                                      {formatCurrency(revisedAmt, "INR")}
                                    </b>
                                  );
                                },
                              },
                            ]}
                            dataSource={matDiscounts}
                            pagination={false}
                            size="small"
                            bordered
                            rowKey={(r: any) => r.bidMaterialId}
                          />
                        );
                      },
                      rowExpandable: (versionRow: any) => (versionRow.materialDiscounts || []).length > 0,
                    }}
                  />
                );
              },
            }}
            pagination={{ pageSize: 5, size: "small", showSizeChanger: true, pageSizeOptions: ["5", "10", "20"] }}
            size="small"
            bordered
          />
        )}
      </Drawer>

      {/* Insights Drawer */}
      <InsightsDrawer
        visible={insightsDrawerVisible}
        onClose={() => setInsightsDrawerVisible(false)}
        suppliers={suppliers}
        evaluationScoreMap={evaluationScoreMap}
        materialNames={materialNames}
        shortlistedData={shortlistedData}
      />

      {/* Status Indicator Drawer */}
      <StatusIndicatorDrawer
        visible={statusIndicatorDrawerVisible}
        onClose={() => setStatusIndicatorDrawerVisible(false)}
        onSubmit={handleStatusIndicatorSubmit}
        initialValues={statusIndicatorRanges}
        loading={indicatorSaving}
      />
    </div>
  );
};

export default TenderComparisonView;
